import React, { useCallback, useState } from "react";

import { Button } from "src/components/buttons";
import { Drawer } from "src/components/drawer";
import { VFlex } from "src/components/vFlex";

import { ClientFilterBar } from "./clientFilterBar";
import { ClientFilterFields } from "./clientFilterFields";
import type { Filter } from "./clientFilterTypes";

export interface ClientFiltersProps {
  readonly filters: Filter[];
}
/**
 * A custom component that renders a filter bar and a drawer with filter fields on screen resize
 * Magnetic FilterBar with onFilterClick function gives Maximum depth exceeded error
 * when the screen is resized, as it has some issue in calculating the width
 * and replacing the filter fields with clickable Filter icon and drawer
 */
export const ClientFilters = ({ filters }: ClientFiltersProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState<boolean>(false);

  const handleClear = useCallback(() => {
    filters.forEach((filter) => {
      filter.setValue("");
    });
  }, [filters]);

  const openDrawer = useCallback(() => {
    setIsDrawerOpen(true);
  }, []);

  const closeDrawer = useCallback(() => {
    setIsDrawerOpen(false);
  }, []);

  const handleCancel = useCallback(() => {
    handleClear();
    closeDrawer();
  }, [handleClear, closeDrawer]);

  const handleTextChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>, filter: Filter) => {
      filter.setValue(e.target.value);
    },
    []
  );

  return (
    <div className="client-filters">
      <ClientFilterBar
        onReset={handleClear}
        onFilterClick={openDrawer}
        showResetButton={filters.some((filter) => filter.value !== "")}
      >
        <ClientFilterFields
          filters={filters}
          handleTextChange={handleTextChange}
        />
      </ClientFilterBar>
      <Drawer isOpen={isDrawerOpen} onClose={closeDrawer}>
        <VFlex gap={4}>
          <Drawer.Heading>Filters</Drawer.Heading>
        </VFlex>
        <Drawer.Content>
          <VFlex gap={4}>
            <ClientFilterFields
              filters={filters}
              handleTextChange={handleTextChange}
            />
            <Drawer.Divider />
            <Button.Group style={{ justifyContent: "flex-end" }}>
              <Button
                name="reset-filters"
                kind="tertiary"
                onClick={handleClear}
                data-qa="reset"
                disabled={filters.every((filter) => filter.value === "")}
              >
                Reset
              </Button>
              <Button
                name="apply-filters"
                kind="primary"
                onClick={closeDrawer}
                data-qa="apply-filters"
                disabled={filters.every((filter) => filter.value === "")}
              >
                Apply
              </Button>
            </Button.Group>
          </VFlex>
        </Drawer.Content>
        <Drawer.Footer>
          <Button
            name="cancel-filters"
            data-qa="cancel"
            kind="tertiary"
            onClick={handleCancel}
          >
            Cancel
          </Button>
        </Drawer.Footer>
      </Drawer>
    </div>
  );
};
